import React, { useEffect, useState, useRef } from "react";
import Card from "../ui/Card";
import Space from "../ui/Space";
import PriceField from "../ui/PriceField";
import { VALIDATE } from "../utils/Validators";
import DateInput from "../ui/DateInput";
import Cell from "../ui/Cell";
import { useTabs } from "../hooks/useTabs";
import Label, { DisplayLabel } from "../ui/Label";
import FocusWrap from "../hooks/FocusWrap";
import Divider from "../ui/Divider";
import { showError, showMessage } from "../ui/Message";
import Input from "../ui/Input";
import dayjs from "dayjs";
import Float from "../ui/Float";
import { pubCall } from "../utils/PubSub";
import { arrayToObject, bm_date, convertDate, drawNote, drawPOSStatus, drawPrice } from "../utils/Utils";
import Status from "../ui/Status";
import { InfoIcon } from "../utils/Icons";
import { KBD } from "../utils/Keybindings";
import { listen } from "../hooks/Listener";

export default function ProcessingDetails(props) {

    const {
        controller = "",
        route = "",
        setSelected = () => { },
        items = [],
        view = false,
        setItems = () => { },
        selectedItem = {},
        switchEdit = () => { },
        edit = false,
        callback = () => { },
    } = props;

    const processing_data = props.selected || {};

    const {
        bill_to_id = "",
        bill_to_san = "",
        ship_to_id = "",
        ship_to_san = "",
        cancel_date = 0,
        cancel_days = 0,
        discount = 0,
        first_received_date = 0,
        order_number = "",
        internal_note = "",
        profit_margin = 0,
        purchase_order_id = "",
        ready = false,
        remind_date = 0,
        send_via = "",
        sent_date = 0,
        separator = "",
        ship_via = "",
        status = "",
        status_date = 0,
        supplier: {
            acct = "",
            address = "",
            alt_bill = 0,
            alt_ship = 0,
            backorder = "",
            cancel_days: supplier_cancel_days = 0,
            code = "",
            consignment = 0,
            currency = "",
            edi_format = "",
            email = "",
            exchange_rate = 0,
            last_active_date = "",
            markup = 0,
            minimum_quantity = 0,
            minimum_retail = 0,
            minimum_type = "",
            name = "",
            note = "",
            note_keynum = "",
            order_via = "",
            payment_supplier = "",
            po_sort = "",
            san = "",
            ship_via: supploer_ship_via = "",
            short_discount_margin = 0,
            tax_group_code = "",
            use_isbn = "",
            vendor_type = "",
            website = ""
        } = {},
        supplier_code = "",
        supplier_type = "",
        total_items = 0,
        total_list_price = 0,
        total_net_price = 0,
        total_sell_price = 0,
        total_special_orders = 0,
    } = processing_data; 

    const cancelDate = dayjs().add((cancel_days) ? cancel_days : supplier_cancel_days, "day").startOf("day").valueOf();

    const defaultTabs = [
        { key: "send_via", value: send_via, skip: false},
        { key: "order_number", value: order_number, skip: sent_date ? false : true},
        { key: "internal_note", value: internal_note, skip: false},
        { key: "backorder", value: backorder, skip: false},
        { key: "cancelDate", value: cancelDate, skip: false },
        { key: "cancelBy", value: cancel_days || supplier_cancel_days, skip: false},
        { key: "remind_date", value: remind_date, skip: false},
    ];

    const statuses = {
        send_via: "Method used to send this order",
        order_number: "PO Number",
        internal_note: "This line is for internal/private notes (and the PromoCode if used) ",
        backorder: "Backorder options: Y=Yes, N=No, B=Only if NYR",
        cancelDate: "Items are cancelled if not shipped by this date (min 5 days from now) ",
        cancelBy: "Days from now until the order is cancelled",
        remind_date: "The order will appear in red when the reminder date arrives. ",
    };

    const sendViaCodes = {
        Rep: "R",
        Web: "W",
        Email: "E", // bypass
        Phone: "P",
        Mail: "M", // bypass
        Fax: "F", // bypass error
        Bisac: "B",
        EDI: "B",
        Net: "N",
    };

    const {
        tabs,
        setTabs,
        index,
        setIndex,
        update,
        getVal,
        editProps,
        next,
        prev,
        refs,
    } = useTabs(defaultTabs, "send_via");

    useEffect(() => {
        setTabs(defaultTabs);
    }, [props.selected]);

    const cancelEdit = () => {
        setTabs(defaultTabs);
        callback(false);
    };

    const dateFields = ["remind_date"];

    const saveEdit = () => {

        const current = refs.current[index];
        let currentValue = current?.value;

        if (dateFields.includes(index) && currentValue) {
            currentValue = convertDate(currentValue, "MM/DD/YY", "YYYY-MM-DD");
        }

        const _tabsObj = arrayToObject(tabs);
        const update = { ..._tabsObj, [index]: currentValue }

        const obj = {
            purchase_order_id: processing_data?.purchase_order_id || "",
            send_via: sendViaCodes[update?.send_via] || update?.send_via || "",
            order_number: update?.order_number || "",
            notes: update?.internal_note || "",
            backorders: update?.backorder || "",
            cancel_by_days: update?.cancelBy || "",
            remind_date: update?.remind_date ? dayjs(update.remind_date).format("YYYY-MM-DD") : "",
        };

        pubCall("purchase_order.edit", obj, (_s, _r) => {
            if (_s) {
                showMessage("Order updated successfully.");
                callback(true, _r);
            } else {
                showError(_r);
            }
        })

    };

    useEffect(() => {
        if (edit && refs?.current?.[index]) {
            refs.current[index].focus();
        }
    }, [index, edit]);

    useEffect(() => {
        if (!edit) { return; }

        const actions = [
            { key: KBD.ESCAPE, action: () => cancelEdit() },
            { key: KBD.PAGE_DOWN, action: () => saveEdit() },
            { key: KBD.ARROW_DOWN, action: () => next() },
            { key: KBD.ARROW_UP, action: () => prev() },
        ];

        const cleanup = listen(
            controller,
            route,
            actions,
            false,
            [
                KBD.ARROW_DOWN,
                KBD.ARROW_UP,
                KBD.PAGE_UP,
                KBD.PAGE_DOWN,
                KBD.ALT_S,
                KBD.ALT_H,
                KBD.ESCAPE,
            ]
        );

        return cleanup;
    }, [index, controller, route, edit, tabs]);

    const daysFromSentDate = Math.abs(
        dayjs(first_received_date)
        .startOf("day")
        .diff(dayjs(sent_date).startOf("day"), "day")
    );
    

    const minimumType = (type) => {
        if (type === "R") {
            return "Retail";
        } else if (type === "N") {
            return "Net";
        }
        return "";
    }

    const sendViaType = (type) => {
        if (type === "Bknet" || type === "Netftp" ) {
            return false;
        } else if (type === "Email") {
            return email;
        } else if (type === "Mail" ) {
            return address;
        } else if (type === "Fax" || type === "Phone") {
            return send_via;
        } else {
            return true;
        }
    }

    const currencyDisplay = (currency) => {
        if (currency === "CA") {
            return "CA";
        } else if (currency === "US") {
            return "US";
        }
        return currency;
    }

    const updateCancelDays = (_days) => {
        const days = Number(_days) || 0;
        update("cancelBy", days);
        update("cancelDate", dayjs().add(days, "day").startOf("day").valueOf());
    }

    const updateCancelDate = (_date) => {
        const days = dayjs(_date).startOf("day").diff(dayjs().startOf("day"), "day");
        update("cancelBy", days);
        update("cancelDate", _date);
    }
    
    return (
        <FocusWrap style={{ "margin": "22px 0 0 20px", "width": "100%", "display": (view) ? "block" : "none", "padding": "0px"}} message={<Status type="active"><InfoIcon />{statuses?.[index] || ""}</Status>} active={edit} >
            <div className="pos-card" style={{"height" : "440px" }} onDoubleClick={switchEdit}>
                <div style={{ display: "flex", flexDirection: "column" }}>

                        <Space>
                            <Label width={95}>Supplier Name</Label>
                            <DisplayLabel style={{ "fontWeight": "bold", "color": "#ffffff " }}>{name}</DisplayLabel>
                            <DisplayLabel style={{ "fontWeight": "bold", "color": "#ffffff " }}>{"(" + currency + ")"}</DisplayLabel>
                            <Label width={40}>SAN</Label>
                            <DisplayLabel style={{ "fontWeight": "bold", "color": "#ffffff " }}>{san}</DisplayLabel>
                        </Space>
                        <div className="shim" />
                        <Space>
                            <Label width={50}>Address</Label>
                            <DisplayLabel style={{textAlign: "left", "fontWeight": "bold", "color": "#ffffff "}} width={sendViaType(send_via) ?  270 : 552}>{address}</DisplayLabel>
                            {/* {sendViaType(send_via) && (
                                <>
                                    <Label width={60}>Send Via</Label>
                                    <DisplayLabel>{sendViaType(send_via)}</DisplayLabel>
                                </>
                            )} */}
                        </Space>
                        <div className="shim" />
                        <Space>
                            <Label width={88}>Supplier Note</Label>
                            <DisplayLabel style={{textAlign: "left", "fontWeight": "bold", "color": "#ffffff "}} width={515}>{note}</DisplayLabel>
                        </Space>

                        <div className="shim" />
                        <hr style={{ "width": "100%" }}/>
                        <div className="shim" />
                                <Space>
                                    <Label width={57}>Send Via</Label>
                                    <Input left width={100} {...editProps("send_via")} edit={edit}/>
                                    <Label width={60}>PO#</Label>
                                    <Input left width={100} {...editProps("order_number")} edit={sent_date ? edit : false}/>
                                </Space>
                                <div className="shim" />
                                <Space> 
                                    <Label width={83}>Internal Note</Label>
                                    <Input width={590} style={{marginLeft: "10px"}} {...editProps("internal_note")} edit={edit}/>
                                </Space>   

                                <div className="shim" />    

                                <Space>
                                    <Label width={25}>B/O</Label>
                                    <Input left maxLength={1} chars={["Y", "N", "B"]} tabMax={() => next()} uppercase width={50} {...editProps("backorder")} edit={edit}/>
                                    <Label width={40}>Cancl</Label>
                                    <DateInput 
                                        width={100} 
                                        timestamp 
                                        {...editProps("cancelDate")}
                                        onFilled={(date) => {
                                            updateCancelDate(date);
                                            next();
                                        }}
                                        edit={edit}
                                    />
                                    <Input width={50} {...editProps("cancelBy")} onChange={updateCancelDays} edit={edit} onEnter={() => next()}/>
                                    <Label width={100}>days from now</Label>                      
                                </Space>
                                <div className="shim" />

                                <Space>
                                    <Label width={40}>Billto# </Label>
                                    <DisplayLabel style={{ textAlign: "center" }} width={25}>{bill_to_id}</DisplayLabel>
                                    <DisplayLabel style={{ textAlign: "left" }} className={bill_to_id !== 0 ? "text-red-bg" : ""} width={95}>{bill_to_id ? bill_to_san : ""}</DisplayLabel>

                                    <Label width={60}>Shipto# </Label>
                                    <DisplayLabel style={{ textAlign: "center" }} width={25}>{ship_to_id}</DisplayLabel>
                                    <DisplayLabel style={{ textAlign: "left" }} className={ship_to_id !== 0 ? "text-red-bg" : ""} width={95}>{ship_to_id ? ship_to_san : ""}</DisplayLabel>
                                </Space>

                                <div className="shim" />

                                <Space>
                                    <Label width={70}>Sent date</Label>
                                    <DisplayLabel 
                                        style={{ textAlign: "left" }}
                                        width={100} 
                                    >  
                                    {bm_date(sent_date)}
                                    </DisplayLabel>
                                    <Label width={80}>Remind me</Label>
                                    <DateInput 
                                        width={100} 
                                        timestamp 
                                        {...editProps("remind_date")}
                                        onFilled={(e) => { update("remind_date", e); next(); }}
                                        edit={edit}
                                    />      
                                </Space> 
                                <div className="shim" />
                                <div className="shim" />
                                <Space>

                                    <Label width={70}>1st Rcvd</Label>
                                    <DisplayLabel 
                                        style={{ textAlign: "left" }}
                                        width={100} 
                                    >  
                                    {bm_date(first_received_date)}
                                    </DisplayLabel>
                                    <DisplayLabel 
                                        style={{ textAlign: "left" }}
                                        width={200} 
                                    >  
                                    {first_received_date && sent_date
                                        ? `${daysFromSentDate} days from Sent date`
                                        : ""}
                                    </DisplayLabel>
                                    
                                </Space>

                        <div className="shim" />
                        <hr className="border-secondary-bg" style={{ opacity: 1, "width": "100%" }}/>
                        <div className="shim" />



                        <table style={{
                            width: "100%",
                            maxWidth: "475px",
                            tableLayout: "fixed",
                            borderCollapse: "separate",
                            fontSize: "16px"
                        }}>
                            <colgroup>
                                <col style={{ width: "45px" }} />
                                <col style={{ width: "60px" }} />
                                <col style={{ width: "115px" }} />
                                <col style={{ width: "115px" }} />
                                <col style={{ width: "85px" }} />
                                <col style={{ width: "50px" }} />
                            </colgroup>
                            <thead>
                                <tr>
                                    {["", "Qty", "Net", "List", "Sell", "PM"].map((header, i) => (
                                        <th key={i} style={{ textAlign: "right" }}>
                                            <Label grow style={{ "fontWeight": 700 }}>{header}</Label>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td style={{ textAlign: "right" }}>
                                        <Label>Min.</Label>
                                    </td>
                                    <td style={{ textAlign: "right", fontWeight: "bold" }}>{minimum_quantity || ""}</td>
                                    <td style={{ textAlign: "right", fontWeight: "bold" }}>{minimum_type === "N" ? drawPrice(minimum_retail) || "" : ""}</td>
                                    <td style={{ textAlign: "right", fontWeight: "bold" }}>{(minimum_type === "R" && minimum_retail) ? drawPrice(minimum_retail) : ""}</td>
                                    <td></td>
                                    <td></td>
                                </tr>
                                <tr>
                                    <td style={{ textAlign: "right" }}>
                                        <Label>Now</Label>
                                    </td>
                                    <td style={{ textAlign: "right", fontWeight: "bold" }} className={minimum_quantity && total_items >= minimum_quantity ? " text-green-bg" : ""}>{total_items}</td>
                                    <td style={{ textAlign: "right", fontWeight: "bold" }} className={minimum_type === "N" && drawPrice(total_net_price, true) >= minimum_retail ? " text-green-bg" : ""}>{drawPrice(total_net_price, true) + " " + (exchange_rate !== 1 ? currencyDisplay(currency) : "") || ""}</td>
                                    <td style={{ textAlign: "right", fontWeight: "bold" }} className={minimum_type === "R" && drawPrice(total_list_price, true) >= minimum_retail ? " text-green-bg" : ""}>{drawPrice(total_list_price, true) + " " + (exchange_rate !== 1 ? currencyDisplay(currency) : "") || ""}</td>
                                    <td style={{ textAlign: "right", fontWeight: "bold" }}>{drawPrice(total_sell_price, true)}</td>
                                    <td style={{ textAlign: "right", fontWeight: "bold" }}>{profit_margin ? profit_margin + "%" : ""}</td>
                                </tr>
                            </tbody>
                        </table>
                        </div>
            </div>
        </FocusWrap>
    )
}
